import { Reveal } from "@/components/common/Reveal";
import { ProjetHero, SaviezVous, ProjetCta } from "@/components/projet/ProjetShared";
import { Check, Ruler, Sun, Trees, ShieldCheck } from "lucide-react";

const HORSE_NEEDS = [
    "Le confort quotidien",
    "La sécurité",
    "La qualité de l'air",
    "La luminosité naturelle",
    "La circulation fluide",
    "Le calme et la sérénité",
];

const ADAPT = [
    "La topographie du terrain",
    "L'orientation du site",
    "Les vents dominants",
    "Le paysage environnant",
    "Les contraintes réglementaires",
    "Le style architectural souhaité",
];

const MANEGE = [
    {
        icon: Ruler,
        title: "Fonctionnalité",
        items: ["Dimensions adaptées à votre discipline", "Circulation optimisée", "Accès simplifiés"],
    },
    {
        icon: Sun,
        title: "Luminosité",
        items: ["Ouvertures étudiées", "Éclairage naturel privilégié", "Confort de travail toute l'année"],
    },
    {
        icon: Trees,
        title: "Intégration paysagère",
        items: ["Volumes harmonieux", "Matériaux nobles", "Insertion naturelle dans le site"],
    },
    {
        icon: ShieldCheck,
        title: "Durabilité",
        items: ["Solutions constructives robustes", "Conçues pour traverser les décennies"],
    },
];

const List = ({ items }) => (
    <ul className="space-y-3">
        {items.map((v) => (
            <li key={v} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-rz-green text-white">
                    <Check size={14} />
                </span>
                <span className="text-base text-rz-ink-soft">{v}</span>
            </li>
        ))}
    </ul>
);

export default function ProjetEquestre() {
    return (
        <main data-testid="projet-equestre-page">
            <ProjetHero
                overline="Chez ROZIER CONCEPT, agricole et haras"
                title="Le cheval au coeur de notre conception."
                img="/images/equestrian1.jpg"
                alt="Cheval dans une écurie sur mesure"
            />

            <section className="bg-rz-bg py-24 lg:py-32 px-6 md:px-12">
                <div className="mx-auto max-w-[1400px] grid gap-12 lg:grid-cols-12 lg:gap-16 items-start">
                    <div className="lg:col-span-7">
                        <Reveal>
                            <h2 className="font-display text-[clamp(1.9rem,4vw,3.2rem)] font-bold tracking-tight leading-[1.08] text-rz-ink">
                                Des infrastructures pensées pour le bien-être du cheval et la
                                performance de son cavalier.
                            </h2>
                        </Reveal>
                        <Reveal delay={0.14}>
                            <p className="mt-7 text-base lg:text-lg leading-relaxed text-rz-ink-soft">
                                Nous concevons des projets équestres uniques, pensés autour du
                                cheval, de son bien-être, de son comportement naturel et des
                                attentes de son propriétaire.
                            </p>
                            <p className="mt-5 text-base lg:text-lg leading-relaxed text-rz-ink-soft">
                                Une écurie, un manège ou un haras ne sont pas de simples
                                constructions. Ils doivent offrir un environnement sain,
                                fonctionnel, durable et harmonieux où chevaux et cavaliers évoluent
                                dans les meilleures conditions. Notre mission consiste à transformer
                                votre vision en un projet cohérent, esthétique et performant, tout en
                                respectant les contraintes techniques, réglementaires et économiques.
                            </p>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-5 lg:pt-2">
                        <Reveal delay={0.2}>
                            <SaviezVous />
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="bg-white py-24 lg:py-32 px-6 md:px-12">
                <div className="mx-auto max-w-[1400px]">
                    <div className="grid gap-12 lg:grid-cols-12 lg:gap-16 items-center">
                        <div className="lg:col-span-6 lg:order-2">
                            <Reveal>
                                <div className="overflow-hidden rounded-[2rem]">
                                    <img
                                        src="/images/hero.jpg"
                                        alt="Écurie sur mesure"
                                        className="h-full w-full object-cover aspect-[4/3] transition-transform [transition-duration:1200ms] hover:scale-105"
                                    />
                                </div>
                            </Reveal>
                        </div>
                        <div className="lg:col-span-6 lg:order-1">
                            <Reveal>
                                <span className="text-xs tracking-[0.22em] uppercase text-rz-sage font-semibold">
                                    Écuries personnalisées
                                </span>
                            </Reveal>
                            <Reveal delay={0.1}>
                                <h2 className="font-display mt-5 text-[clamp(1.8rem,3.5vw,2.8rem)] font-bold tracking-tight leading-[1.08] text-rz-ink">
                                    Une écurie à l'image de votre passion.
                                </h2>
                            </Reveal>
                            <Reveal delay={0.16}>
                                <p className="mt-6 text-base lg:text-lg leading-relaxed text-rz-ink-soft">
                                    Chaque cheval est unique, chaque cavalier possède sa propre
                                    vision, chaque propriété présente des caractéristiques
                                    particulières. C'est pourquoi nous ne proposons pas de modèle
                                    standard : nous concevons des écuries entièrement personnalisées,
                                    pensées autour de vos objectifs, de vos chevaux et de votre
                                    domaine. Un lieu de vie harmonieux, fonctionnel et durable où le
                                    bien-être du cheval rencontre l'excellence architecturale.
                                </p>
                            </Reveal>
                        </div>
                    </div>

                    <div className="mt-20 grid gap-12 lg:grid-cols-2 lg:gap-16">
                        <Reveal>
                            <div>
                                <h3 className="font-display text-xl font-bold text-rz-ink">
                                    Conçue autour du cheval
                                </h3>
                                <p className="mt-3 mb-6 text-sm text-rz-ink-soft leading-relaxed">
                                    Le cheval est un athlète, un partenaire et un être sensible à son
                                    environnement. Chaque projet est étudié pour favoriser :
                                </p>
                                <List items={HORSE_NEEDS} />
                            </div>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <div>
                                <h3 className="font-display text-xl font-bold text-rz-ink">
                                    Adaptée à votre site
                                </h3>
                                <p className="mt-3 mb-6 text-sm text-rz-ink-soft leading-relaxed">
                                    Parce qu'aucun projet ne ressemble à un autre, nous adaptons
                                    chaque réalisation à :
                                </p>
                                <List items={ADAPT} />
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="bg-rz-bg py-24 lg:py-32 px-6 md:px-12">
                <div className="mx-auto max-w-[1400px]">
                    <div className="max-w-2xl mb-14">
                        <Reveal>
                            <span className="text-xs tracking-[0.22em] uppercase text-rz-sage font-semibold">
                                Manèges équestres
                            </span>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h2 className="font-display mt-5 text-[clamp(1.9rem,4vw,3.2rem)] font-bold tracking-tight leading-[1.05] text-rz-ink">
                                Esthétiques, performants et éthiques.
                            </h2>
                        </Reveal>
                        <Reveal delay={0.16}>
                            <p className="mt-5 text-base lg:text-lg leading-relaxed text-rz-ink-soft">
                                Le manège représente le coeur technique de nombreuses structures
                                équestres. Nous réalisons des conceptions sur mesure intégrant :
                            </p>
                        </Reveal>
                    </div>

                    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                        {MANEGE.map((m, i) => (
                            <Reveal key={m.title} delay={i * 0.08}>
                                <div className="h-full rounded-2xl border border-black/5 bg-white p-7 transition-all duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-rz-green/5">
                                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-rz-pale/40 text-rz-green">
                                        <m.icon size={22} />
                                    </span>
                                    <h3 className="font-display mt-6 text-lg font-bold text-rz-ink">
                                        {m.title}
                                    </h3>
                                    <ul className="mt-4 space-y-2">
                                        {m.items.map((it) => (
                                            <li key={it} className="text-sm leading-relaxed text-rz-ink-soft">
                                                {it}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <ProjetCta text="Concevons votre projet dans les règles de l'art, pour le bien-être de votre animal." />
        </main>
    );
}
